/**
 * Roles and permissions.
 *
 * The matrix is ported verbatim from the Base44 build
 * (`src/pages/UserManagement.jsx:136-172`), but where that version was a
 * frontend-only convenience — the sidebar hid links and nothing else — this
 * module is imported by the API middleware too. Hiding a nav item is not
 * access control.
 *
 * Base44 also carried two parallel role fields: its built-in `role`
 * ("admin"/"user") and this app's `user_role`. The nav gate honoured the former
 * while the UI displayed the latter. There is now exactly one field.
 */

export const USER_ROLES = Object.freeze([
  'admin',
  'manager',
  'receiver',
  'allocator',
  'viewer',
]);

export const ROLE_LABELS = Object.freeze({
  admin: 'Administrator',
  manager: 'Manager',
  receiver: 'Receiver',
  allocator: 'Allocator',
  viewer: 'Viewer',
});

export const ROLE_COLORS = Object.freeze({
  admin:
    'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800',
  manager:
    'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800',
  receiver:
    'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800',
  allocator:
    'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
  viewer:
    'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600',
});

/** Every permission flag, in the order the admin UI shows them. */
export const PERMISSION_FLAGS = Object.freeze([
  'can_view_products',
  'can_edit_products',
  'can_view_vendors',
  'can_edit_vendors',
  'can_view_pos',
  'can_edit_pos',
  'can_order',
  'can_receive',
  'can_allocate',
  'can_manage_users',
]);

export const PERMISSION_LABELS = Object.freeze({
  can_view_products: 'View products',
  can_edit_products: 'Add & edit products',
  can_view_vendors: 'View vendors',
  can_edit_vendors: 'Add & edit vendors',
  can_view_pos: 'View purchase orders',
  can_edit_pos: 'Edit purchase orders',
  can_order: 'Place orders & regenerate drafts',
  can_receive: 'Receive shipments',
  can_allocate: 'Allocate stock',
  can_manage_users: 'Manage users',
});

const NONE = PERMISSION_FLAGS.reduce((acc, flag) => {
  acc[flag] = false;
  return acc;
}, {});

const ALL = PERMISSION_FLAGS.reduce((acc, flag) => {
  acc[flag] = true;
  return acc;
}, {});

/** Default permission set applied when a role preset is chosen. */
export const ROLE_PRESETS = Object.freeze({
  admin: Object.freeze({ ...ALL }),
  manager: Object.freeze({ ...ALL, can_manage_users: false }),
  receiver: Object.freeze({
    ...NONE,
    can_view_products: true,
    can_view_vendors: true,
    can_view_pos: true,
    can_receive: true,
  }),
  allocator: Object.freeze({
    ...NONE,
    can_view_products: true,
    can_allocate: true,
  }),
  viewer: Object.freeze({
    ...NONE,
    can_view_products: true,
    can_view_vendors: true,
    can_view_pos: true,
  }),
});

export function emptyPermissions() {
  return { ...NONE };
}

export function presetFor(role) {
  return { ...(ROLE_PRESETS[role] || ROLE_PRESETS.viewer) };
}

/**
 * Normalise a stored permissions blob into a complete, boolean-valued object.
 * Base44 allowed partial objects and missing keys; this fills the gaps.
 */
export function normalizePermissions(permissions, role) {
  const base = presetFor(role);
  if (!permissions || typeof permissions !== 'object') return base;
  const out = { ...NONE };
  for (const flag of PERMISSION_FLAGS) {
    out[flag] = permissions[flag] === true;
  }
  return out;
}

/**
 * The single authorization predicate, used by both the sidebar and the API.
 *
 * Deactivated accounts are denied everything — in the Base44 build `is_active`
 * was editable in the admin UI but never actually checked anywhere.
 */
export function hasPermission(profile, flag) {
  if (!profile) return false;
  if (profile.is_active === false) return false;
  if (!flag) return true;
  if (profile.user_role === 'admin') return true;
  return profile.permissions?.[flag] === true;
}

export function isAdmin(profile) {
  return !!profile && profile.is_active !== false && profile.user_role === 'admin';
}
